import { useMemo, useState, useCallback, useEffect } from "react";

import { cloneDeep } from "lodash";

import { useCellSelection, type CellIdentifier } from "./use-cell-selection";

interface HasId {
  id: string;
}

export type EditedCell<T> = CellIdentifier & {
  value: T;
};

// NOTE: This is being used, in short, because the built-in editing
// functionality of DevExtreme does not, in its most vanilla form, fit
// the usecase for the functionality we want, and the docs and design are not
// such that it is trivial to use their architecture to achieve it.

// Here is the best source I could find to try to use their setup (inline cell editing is most similar
// to what we are trying to achieve)
// https://devexpress.github.io/devextreme-reactive/react/grid/docs/guides/editing/#inline-cell-editing

// Some unresolved issues:
// - Sandbox link doesn't work for messing with code
// - It appears that this example never commits the changes, and does not
//   describe how to do so
// - Using some controlled components which should be able to enable custom commit
//   calls (rowChanges and onRowChangesChange) seems to causes a table re-render after each
//   keystroke edit
// - Defining which columns can be edited doesn't work, so hacky code
//   would be needed to prevent editing on any column

export const useEditableCell = <T>(cell: CellIdentifier) => {
  const [, , { addCell, getCell }] = useCellSelection<EditedCell<T>>();

  const editedCell: EditedCell<T> = getCell(cell);

  const updateEditedValue = useCallback(
    (newValue: T) => {
      addCell({
        ...cell,
        value: newValue,
      });
    },
    [cell, addCell],
  );

  return {
    editedValue: editedCell?.value,
    updateEditedValue,
  };
};

export const useEditableCells = <T, R extends HasId>(
  rows: R[],
  columnNamesToRowValueMapper: (row: R, columnName: string) => T,
  mergeCellToRow: (row: R, cell: EditedCell<T>) => void,
  commitEdits: (newRows: R[]) => void,
) => {
  const mappedRows = useMemo(
    () => new Map(rows.map((row) => [row.id, cloneDeep(row)])),
    [rows],
  );

  const [
    editedCells,
    ,
    {
      getCell: getEditedCell,
      addCell: addEditedCell,
      clearCells: clearEditedCells,
    },
  ] = useCellSelection<EditedCell<T>>();

  const getDefaultCellValue = useCallback(
    (cell: CellIdentifier) => {
      const row = mappedRows.get(cell.rowId);
      if (!row) {
        // TODO: Consider throwing error here
        return undefined;
      }
      return columnNamesToRowValueMapper(row, cell.columnName);
    },
    [mappedRows, columnNamesToRowValueMapper],
  );

  const getCellValue = useCallback(
    (cell: CellIdentifier) => {
      const editedCell = getEditedCell(cell);
      if (editedCell) {
        return editedCell.value;
      }

      return getDefaultCellValue(cell);
    },
    [getEditedCell, getDefaultCellValue],
  );

  const setCellValue = useCallback(
    (cell: EditedCell<T>) => {
      addEditedCell(cell);
    },
    [addEditedCell],
  );

  const previewCommit = useCallback(() => {
    const mappedAlteredRows = new Map(
      editedCells.map((cell) => {
        const row = cloneDeep(mappedRows.get(cell.rowId));
        if (!row) {
          throw new Error("Error finding row");
        }
        mergeCellToRow(row, cell);
        return [cell.rowId, row];
      }),
    );
    return rows.map((row) => {
      const alteredRow = mappedAlteredRows.get(row.id);
      return alteredRow ? alteredRow : cloneDeep(row);
    });
  }, [rows, editedCells, mappedRows, mergeCellToRow]);

  const commitChanges = useCallback(() => {
    const mappedAlteredRows = new Map(
      editedCells.map((cell) => {
        const row = cloneDeep(mappedRows.get(cell.rowId));
        if (!row) {
          throw new Error("Error finding row");
        }
        mergeCellToRow(row, cell);
        return [cell.rowId, row];
      }),
    );
    commitEdits(
      rows.map((row) => {
        const alteredRow = mappedAlteredRows.get(row.id);
        return alteredRow ? alteredRow : row;
      }),
    );
    clearEditedCells();
  }, [
    clearEditedCells,
    rows,
    editedCells,
    mappedRows,
    commitEdits,
    mergeCellToRow,
  ]);

  return {
    editedCells,
    getCellValue,
    setCellValue,
    commitChanges,
    previewCommit,
  };
};
